import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatNOK, formatDateShort } from "@/lib/format";
import { OFFER_PROBABILITY_LABELS } from "@/lib/offer-pipeline-calc";
import { useOffersInPipe } from "@/hooks/use-offers-in-pipe";
import type { AdvisorPipe } from "@/lib/offers-in-pipe";

interface Props {
  nameOf: (userId: string) => string;
  /** Feature-flagget avgjør; panelet rendrer ingenting når det er av. */
  enabled: boolean;
}

/**
 * VIB-2094: «Tilbud i pipe» per rådgiver.
 *
 * William (01.09) ba om én linje per rådgiver som kan åpnes for å se hvilke
 * tilbud som ligger ute, med GEEK, sannsynlighet og estimert close-dato.
 *
 * Vektet GEEK er tallet å styre etter, så det står først og rådgiverne
 * sorteres på det. Rå GEEK står ved siden av, fordi forskjellen mellom de to
 * er hele poenget med å vekte.
 */
export default function OffersInPipePanel({ nameOf, enabled }: Props) {
  const { pipes, totals, isLoading } = useOffersInPipe(enabled);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  if (!enabled) return null;

  const toggle = (userId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-baseline justify-between gap-2 text-base">
          <span>Tilbud i pipe</span>
          {!isLoading && totals.offerCount > 0 && (
            <span className="text-sm font-normal text-muted-foreground">
              {totals.offerCount} tilbud ute
            </span>
          )}
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="h-28 animate-pulse rounded bg-muted" />
        ) : pipes.length === 0 ? (
          <p className="text-sm text-muted-foreground">Ingen tilbud ute nå.</p>
        ) : (
          <>
            <div className="flex items-baseline justify-between border-b pb-2">
              <span className="text-sm text-muted-foreground">
                Vektet GEEK
                <span className="ml-2 text-xs">av {formatNOK(totals.bfKr)} totalt</span>
              </span>
              <span className="text-lg font-semibold tabular-nums">
                {formatNOK(totals.weightedBfKr)}
              </span>
            </div>

            <ul className="divide-y">
              {pipes.map((pipe) => (
                <AdvisorRow
                  key={pipe.userId}
                  pipe={pipe}
                  name={nameOf(pipe.userId)}
                  open={expanded.has(pipe.userId)}
                  onToggle={() => toggle(pipe.userId)}
                />
              ))}
            </ul>

            {(totals.uncategorizedCount > 0 || totals.missingCloseDateCount > 0) && (
              <p className="pt-1 text-xs text-muted-foreground">
                {totals.uncategorizedCount > 0 && (
                  <>
                    {totals.uncategorizedCount} tilbud mangler sannsynlighet og teller
                    ikke i vektet GEEK.
                  </>
                )}
                {totals.uncategorizedCount > 0 && totals.missingCloseDateCount > 0 && " "}
                {totals.missingCloseDateCount > 0 && (
                  <>{totals.missingCloseDateCount} mangler estimert close-dato.</>
                )}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function AdvisorRow({
  pipe,
  name,
  open,
  onToggle,
}: {
  pipe: AdvisorPipe;
  name: string;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 py-2 text-left hover:bg-muted/40"
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <SrIcon
            name={open ? "chevron-up" : "chevron-down"}
            className="h-4 w-4 flex-shrink-0 text-muted-foreground"
          />
          <span className="truncate text-sm font-medium">{name}</span>
          <span className="flex-shrink-0 text-xs text-muted-foreground">
            ({pipe.offers.length})
          </span>
        </span>
        <span className="flex-shrink-0 text-sm tabular-nums">
          {formatNOK(pipe.weightedBfKr)}
          <span className="ml-2 text-xs text-muted-foreground">
            av {formatNOK(pipe.bfKr)}
          </span>
        </span>
      </button>

      {open && (
        <ul className="space-y-2 pb-3 pl-6 pr-1">
          {pipe.offers.map((o) => (
            <li key={o.id} className="text-xs">
              <div className="flex items-baseline justify-between gap-2">
                <span className="min-w-0 truncate font-medium text-foreground">
                  {o.title}
                  {o.customerName && (
                    <span className="font-normal text-muted-foreground">
                      {" "}
                      · {o.customerName}
                    </span>
                  )}
                  {o.sharePct < 100 && (
                    <span className="font-normal text-muted-foreground">
                      {" "}
                      · {o.sharePct} %
                    </span>
                  )}
                </span>
                <span className="flex-shrink-0 tabular-nums">
                  {o.weightedBfKr == null ? "—" : formatNOK(o.weightedBfKr)}
                  <span className="ml-1.5 text-muted-foreground">
                    av {formatNOK(o.bfKr)}
                  </span>
                </span>
              </div>
              <div className="mt-0.5 flex flex-wrap gap-x-3 text-muted-foreground">
                <span>
                  {o.probability
                    ? OFFER_PROBABILITY_LABELS[o.probability]
                    : "Sannsynlighet ikke satt"}
                </span>
                <span>
                  {o.expectedCloseDate
                    ? `Close ${formatDateShort(o.expectedCloseDate)}`
                    : "Ingen close-dato"}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
